import { isValidElement, useRef } from "react"
import { useTheme } from "next-themes"
import {
  Toaster as Sonner,
  toast,
  useSonner,
  type Action,
  type ToastT,
  type ToastToDismiss,
} from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

function isUndoAction(action: React.ReactNode | Action): action is Action {
  if (!action || typeof action !== "object" || isValidElement(action)) return false
  if (!("label" in action) || !("onClick" in action)) return false
  return action.label === "Undo" || action.label === "Ongedaan maken"
}

function hasUndoAction(item: ToastT | ToastToDismiss): item is ToastT & { action: Action } {
  return "action" in item && isUndoAction(item.action)
}

/** Mirrors the latest offered Undo inside a modal focus scope. */
const DialogToastAction = () => {
  // Subscribe for live rerenders; the hook starts empty, so seed reopen state from the active snapshot.
  useSonner()
  const consumed = useRef(new Set<string | number>())
  const offered = [...toast.getToasts()].reverse().find(hasUndoAction)

  if (!offered) return null
  const action = offered.action
  const title = typeof offered.title === "function" ? offered.title() : offered.title

  return (
    <div
      className="mx-3 mt-3 flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-2 text-xs shadow-sm"
      data-dialog-toast-action
    >
      <span className="min-w-0 truncate font-medium">{title}</span>
      <button
        type="button"
        className="shrink-0 rounded-md bg-primary px-2.5 py-1.5 font-semibold text-primary-foreground"
        data-track="planning-board-undo"
        onClick={(event) => {
          if (consumed.current.has(offered.id)) return
          consumed.current.add(offered.id)
          toast.dismiss(offered.id)
          action.onClick(event)
        }}
      >
        {action.label}
      </button>
    </div>
  )
}

export { DialogToastAction, Toaster, toast }
